import {
  REACT_APP_MONOLITH_BASE_URL,
  REACT_APP_SIGN_OUT_REDIRECT_URL,
} from 'runtimeEnvVars';

import { TRACKING_VISITOR_KEY } from 'utils/constants';
import {
  getAndClearIntendedDestination,
  saveIntendedDestination,
} from 'utils/intendedDestination';
import { getSessionItem } from 'utils/storageUtils';

// Holds when this tab last left for sign-in after a 401.
const LAST_REDIRECT_KEY = 'fountain:auth:unauthorized_redirect_at';

// A 401 this soon after the last one means sign-in handed back a session the
// API rejects again, such as a WX token Hire refuses. One sign-in round trip
// takes seconds; a person signing in again by hand takes longer.
export const LOOP_WINDOW_MS = 60 * 1000;

// Ends on /signin, so the tab waits for a person instead of looping. It revokes
// the WX session only while Hire still has a signed-in user: after Devise's own
// 401 the Hire session that names the WX session is gone.
const WX_LOGOUT_URL = `${REACT_APP_MONOLITH_BASE_URL}/wx/logout`;

// A page fires many requests at once, and a "Leave site?" prompt can keep it
// open after a redirect. Only a 401 on a later page load counts as a loop.
const newPageLoadId = (): string => `${Date.now()}-${Math.random()}`;
let pageLoadId = newPageLoadId();
// Once this page finds the loop, its later 401s keep going to /wx/logout.
// A plain sign-in redirect would replace that pending navigation.
let pageFoundLoop = false;

interface LastRedirect {
  at: number;
  pageLoadId: string;
}

const readLastRedirect = (): LastRedirect | null => {
  try {
    const raw = window.sessionStorage.getItem(LAST_REDIRECT_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as LastRedirect).at === 'number' &&
      typeof (parsed as LastRedirect).pageLoadId === 'string'
    ) {
      return parsed as LastRedirect;
    }
    return null;
  } catch {
    return null;
  }
};

const writeLastRedirect = (lastRedirect: LastRedirect): void => {
  try {
    window.sessionStorage.setItem(
      LAST_REDIRECT_KEY,
      JSON.stringify(lastRedirect),
    );
  } catch {
    // Without storage the guard cannot see a loop; the redirect still happens.
  }
};

/**
 * Sends the browser to sign in after an API call answers 401.
 *
 * The first 401 keeps the page as the destination to return to after sign-in.
 * A 401 on a later page load inside `LOOP_WINDOW_MS` goes to `/wx/logout`
 * instead and drops the destination, so the tab stops on /signin.
 */
export function redirectAfterUnauthorized(): void {
  const now = Date.now();
  const lastRedirect = readLastRedirect();
  writeLastRedirect({ at: now, pageLoadId });

  if (
    pageFoundLoop ||
    (lastRedirect !== null &&
      lastRedirect.pageLoadId !== pageLoadId &&
      now - lastRedirect.at < LOOP_WINDOW_MS)
  ) {
    pageFoundLoop = true;
    getAndClearIntendedDestination();
    window.location.replace(WX_LOGOUT_URL);
    return;
  }

  saveIntendedDestination();
  const visitorTrack = getSessionItem(TRACKING_VISITOR_KEY);
  window.location.replace(
    `${REACT_APP_SIGN_OUT_REDIRECT_URL}?app=${visitorTrack}`,
  );
}

/** Test seam: stands for the browser loading a new page in the same tab. */
export function startNewPageLoadForTest(): void {
  pageLoadId = newPageLoadId();
  pageFoundLoop = false;
}
